import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { Navigation } from '@/models/navigation'
import type { Locale } from '@/router/locales'
import { NavigationService } from '@/services/navigation.service'
import { useLocaleStore } from '@/stores/locale.store'

export const NAVIGATION_LOCATIONS = {
  primary: 'primary_navigation',
  mobile: 'mobile_navigation',
  footer: 'footer_navigation',
} as const

type NavigationCache = Partial<Record<Locale, {
  fetchedAt: number
  navigations: Navigation[]
}>>

const CACHE_TTL_MS = 5 * 60_000

export const useNavigationStore = defineStore('navigation', () => {
  const localeStore = useLocaleStore()
  const cache = ref<NavigationCache>({})
  const errors = ref<Partial<Record<Locale, unknown>>>({})
  const loadingCount = ref(0)
  const inFlight = new Map<Locale, Promise<void>>()

  const currentCache = computed(() => cache.value[localeStore.currentLocale])
  const navigations = computed(() => currentCache.value?.navigations ?? [])
  const isLoading = computed(() => loadingCount.value > 0)
  const error = computed(() => errors.value[localeStore.currentLocale])
  const hasLoaded = computed(() => Boolean(currentCache.value?.fetchedAt))

  function isFresh(locale: Locale) {
    const entry = cache.value[locale]
    return Boolean(entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS)
  }

  async function ensure(options?: { force?: boolean }) {
    const locale = localeStore.currentLocale

    if (!options?.force && isFresh(locale)) return

    const existing = inFlight.get(locale)
    if (existing) return existing

    loadingCount.value += 1
    errors.value[locale] = undefined

    const promise = (async () => {
      try {
        const result = await NavigationService.get(locale)
        cache.value[locale] = {
          fetchedAt: Date.now(),
          navigations: result,
        }
      } catch (caught) {
        errors.value[locale] = caught
      } finally {
        loadingCount.value = Math.max(0, loadingCount.value - 1)
        inFlight.delete(locale)
      }
    })()

    inFlight.set(locale, promise)
    return promise
  }

  function findByLocation(location: string): Navigation | null {
    return navigations.value.find((navigation) => navigation.locations.includes(location)) ?? null
  }

  function invalidate(locale?: Locale) {
    if (locale) {
      delete cache.value[locale]
      return
    }

    cache.value = {}
  }

  return {
    navigations,
    isLoading,
    error,
    hasLoaded,
    ensure,
    findByLocation,
    invalidate,
  }
})
